import { useRef } from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import { Play, Radio, Eye, Globe } from "lucide-react";
import { Link } from "react-router-dom";
import AppStoreButton from "./AppStoreButton";

const SCREEN_IMG =
  "https://images.unsplash.com/photo-1536440136628-849c177e76a1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwxfHxjaW5lbWElMjByZWQlMjBsaWdodCUyMGRhcmslMjBuZW9uJTIwc3R1ZGlvfGVufDB8fHx8MTc5MjAwMzA1OXww&ixlib=rb-4.1.0&q=85";

const LINES = ["STREAMLINE", "YOUR CINEMATIC", "EXPERIENCE"];

const lineVariant = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: "0%",
    transition: { delay: 0.35 + i * 0.13, duration: 0.9, ease: [0.22, 1, 0.36, 1] },
  }),
};

const PhoneMockup = () => {
  const ref = useRef(null);
  const rotateX = useSpring(0, { stiffness: 120, damping: 16 });
  const rotateY = useSpring(0, { stiffness: 120, damping: 16 });

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    rotateY.set(((e.clientX - r.left) / r.width - 0.5) * 16);
    rotateX.set(-((e.clientY - r.top) / r.height - 0.5) * 12);
  };
  const onLeave = () => {
    rotateX.set(0);
    rotateY.set(0);
  };

  return (
    <div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      style={{ perspective: 1200 }}
      className="relative flex justify-center"
      data-testid="phone-mockup-container"
    >
      <div className="absolute top-1/2 left-1/2 h-[420px] w-[420px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-crimson/25 blur-[120px]" />
      <motion.div
        initial={{ opacity: 0, y: 60, rotate: 4 }}
        animate={{ opacity: 1, y: 0, rotate: 0 }}
        transition={{ delay: 0.7, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
        style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
        className="relative w-[270px] sm:w-[310px]"
      >
        <div className="rounded-[3rem] border border-white/15 bg-black p-2.5 shadow-[0_40px_120px_rgba(0,0,0,0.8)]">
          <div className="relative aspect-[9/19] overflow-hidden rounded-[2.4rem] bg-surface">
            <img src={SCREEN_IMG} alt="Michael C Streamline app streaming a cinematic premiere" className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-transparent to-black/85" />
            <div className="absolute left-1/2 top-2.5 h-5 w-24 -translate-x-1/2 rounded-full bg-black" />
            <div className="absolute left-4 top-9 flex items-center gap-1.5 rounded-full bg-crimson px-2.5 py-1">
              <Radio size={11} strokeWidth={2.5} />
              <span className="font-mono text-[9px] font-medium uppercase tracking-widest text-white">Live</span>
            </div>
            <div className="absolute right-4 top-9 flex items-center gap-1 rounded-full bg-black/50 px-2.5 py-1 backdrop-blur-sm">
              <Eye size={11} className="text-slate-300" />
              <span className="font-mono text-[9px] text-slate-200">12.4K</span>
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-full border border-white/30 bg-white/10 backdrop-blur-md">
                <Play size={20} className="ml-0.5 text-white" fill="white" />
              </div>
            </div>
            <div className="absolute inset-x-4 bottom-5">
              <p className="font-heading text-sm font-bold text-white">Midnight Premiere</p>
              <p className="mt-0.5 font-mono text-[9px] uppercase tracking-[0.2em] text-slate-400">Now Streaming · 4K HDR</p>
              <div className="mt-3 h-1 overflow-hidden rounded-full bg-white/20">
                <motion.div
                  initial={{ width: "8%" }}
                  animate={{ width: "64%" }}
                  transition={{ delay: 1.6, duration: 2.4, ease: "easeOut" }}
                  className="h-full rounded-full bg-crimson"
                />
              </div>
            </div>
          </div>
        </div>
        <div
          style={{ transform: "translateZ(50px)" }}
          className="absolute -right-10 top-16 rounded-xl border border-white/10 bg-white/[0.06] px-3 py-2 backdrop-blur-md"
        >
          <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-crimson">4K HDR</p>
          <p className="font-heading text-xs font-bold text-white">Ultra Clarity</p>
        </div>
        <div
          style={{ transform: "translateZ(70px)" }}
          className="absolute -left-12 bottom-24 rounded-xl border border-white/10 bg-white/[0.06] px-3 py-2 backdrop-blur-md"
        >
          <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-slate-400">Community</p>
          <p className="font-heading text-xs font-bold text-white">2M+ Creators</p>
        </div>
      </motion.div>
    </div>
  );
};

const Hero = () => {
  const { scrollY } = useScroll();
  const yPhone = useTransform(scrollY, [0, 700], [0, 90]);

  return (
    <section id="top" className="spotlight relative overflow-hidden" data-testid="hero-section">
      <div className="noise-overlay" />
      <div className="mx-auto grid max-w-7xl items-center gap-16 px-6 pb-24 pt-40 sm:pt-44 lg:grid-cols-[1.1fr_0.9fr] lg:pb-32 lg:pt-48">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, duration: 0.7 }}
            className="mb-8 inline-flex items-center gap-2.5 rounded-full border border-crimson/30 bg-crimson/10 px-4 py-1.5"
            data-testid="hero-platform-tag"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-crimson opacity-60" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-crimson" />
            </span>
            <span className="font-mono text-[11px] font-medium uppercase tracking-[0.25em] text-crimson">
              Exclusively on iOS
            </span>
          </motion.div>

          <h1 className="font-heading text-[13vw] font-black leading-[0.95] tracking-tight text-white sm:text-7xl lg:text-[5.2rem]" data-testid="hero-headline">
            {LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  custom={i}
                  variants={lineVariant}
                  initial="hidden"
                  animate="visible"
                  className={`block ${i === 2 ? "text-glow-crimson text-crimson" : ""}`}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="mt-8 max-w-xl text-base leading-relaxed text-slate-400 sm:text-lg"
            data-testid="hero-description"
          >
            Michael C Streamline is a premier video streaming and social media
            platform engineered natively for iOS — high-definition streaming,
            interactive social feeds, and real-time creator connections, all in
            one cinematic experience.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.8 }}
            className="mt-10 flex flex-wrap items-center gap-5"
          >
            <AppStoreButton variant="light" size="lg" testId="hero-download-button" />
            <Link
              to="/app"
              data-testid="hero-open-web-button"
              className="inline-flex items-center gap-2.5 rounded-full border border-white/20 px-7 py-4 font-heading text-sm font-bold text-white transition-colors duration-300 hover:border-crimson hover:text-crimson"
            >
              <Globe size={16} />
              Open in browser
            </Link>
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">
              Free · Requires iOS 16+
            </span>
          </motion.div>
        </div>

        <motion.div style={{ y: yPhone }}>
          <PhoneMockup />
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
